// ────────────────────────────────────────────────────────────────────────────
// Which bank account prints on a document.
//
// Selection is by **currency**: an account is held in one currency, a document
// is denominated in one currency, and telling a customer to pay a GBP invoice
// into a EUR account is worse than telling them nothing. So there is
// deliberately NO cross-currency fallback — a currency with no account prints
// no payment block, and the builder says why.
//
// `isDefaultForCurrency` only matters when an organisation holds more than one
// account in the same currency. With one account the resolver uses it whether
// or not anything is flagged, so the common case needs no setup.
// ────────────────────────────────────────────────────────────────────────────
import type { OrganisationBankAccount } from '@my/directory-api';

export type BankAccountStatus =
  /** One account in this currency, or one flagged as its default. */
  | 'resolved'
  /** Several in this currency and none flagged — printing a stable guess. */
  | 'ambiguous'
  /** No account in this currency; nothing will print. */
  | 'none';

export interface BankAccountSelection {
  account: OrganisationBankAccount | null;
  status: BankAccountStatus;
  /** How many accounts the organisation holds in this currency. */
  candidates: number;
}

/**
 * Resolve the account for `currencyIsoCode`.
 *
 * The `ambiguous` pick is sorted by name rather than left in API order: the
 * document must print the same account on every render, and list order is not a
 * promise anyone has made.
 */
export function selectBankAccount(
  accounts: readonly OrganisationBankAccount[] | undefined,
  currencyIsoCode: string | null | undefined,
): BankAccountSelection {
  if (!accounts?.length || !currencyIsoCode) {
    return { account: null, status: 'none', candidates: 0 };
  }

  const currency = currencyIsoCode.toUpperCase();
  const inCurrency = accounts
    .filter((a) => (a.currencyIsoCode ?? '').toUpperCase() === currency)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (inCurrency.length === 0) {
    return { account: null, status: 'none', candidates: 0 };
  }

  const flagged = inCurrency.filter((a) => a.isDefaultForCurrency);
  if (flagged.length > 0) {
    // More than one flagged should be impossible — the BE clears siblings on
    // write — but if history left two, the sorted first keeps the output stable.
    return { account: flagged[0]!, status: 'resolved', candidates: inCurrency.length };
  }

  if (inCurrency.length === 1) {
    return { account: inCurrency[0]!, status: 'resolved', candidates: 1 };
  }

  return { account: inCurrency[0]!, status: 'ambiguous', candidates: inCurrency.length };
}
